// "Link teilen" and "Suchprofile": the catalog's search state as one plain object, written to and
// read from URL parameters (a link that reopens exactly this search) and stored under a name in
// the browser (localStorage, see searchProfilesAtom). Both use the same snapshot, so a saved
// profile and a shared link always restore the same things.
//
// Deliberately NOT part of the snapshot: the page number and page size - a link opens at the top
// of the list. The table's column filters ARE part of it: they show as chips like every other
// filter, and a link has to reproduce what the chips say.

import { useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { useCallback, useMemo } from 'react';

import {
    columnFiltersAtom,
    documentDocTypesAtom,
    documentEditionsAtom,
    documentLanguageAtom,
    filtersAtom,
    listColumnsAtom,
    pageAtom,
    scopeNodeIdsAtom,
    searchAtom,
    searchModeAtom,
    selectedDocumentNodeIdAtom,
    selectedNodeIdAtom,
    sortAtom,
    viewModeAtom
} from './state';

import type { SortState } from './engine';
import type { ActiveFilters, CatalogConfig, DateRangeValue, FacetDef, FilterValue, RangeValue } from './types';

export interface CatalogSearchState {
    mode: 'products' | 'documents';
    search: string;
    nodeId?: string;
    documentNodeId?: string;
    scopeNodeIds: string[];
    filters: ActiveFilters;
    documentLanguage: string | null;
    documentDocTypes: string[];
    documentEditions: string[];
    sort: SortState;
    viewMode: 'grid' | 'list';
    listColumns: string[];
    /** The table's column filters (header funnels): column key -> text, or the picked value. */
    columnFilters: Record<string, string>;
}

/** The defaults of the atoms in state.ts - a parameter is only written when it differs, so a link
 * stays as short as the search it describes. */
const DEFAULTS: CatalogSearchState = {
    mode: 'products',
    search: '',
    scopeNodeIds: [],
    filters: {},
    documentLanguage: null,
    documentDocTypes: [],
    documentEditions: [],
    sort: { key: 'name', dir: 'asc' },
    viewMode: 'list',
    listColumns: [],
    columnFilters: {}
};

// Short, readable parameter names. `preset` (CatalogPage's assortment deep link) is a separate,
// older mechanism and stays out of this list.
const P = {
    mode: 'mode',
    search: 'q',
    nodeId: 'node',
    documentNodeId: 'docnode',
    scopeNodeIds: 'scope',
    filters: 'f',
    documentLanguage: 'lang',
    documentDocTypes: 'doctype',
    documentEditions: 'edition',
    sort: 'sort',
    viewMode: 'view',
    listColumns: 'col'
} as const;

/** Facet filters go as one parameter per facet, `f.<facet key>` - readable in a link that is
 * passed around by mail: `f.subsidiary=Doka+Corporate&f.laenge=2.5..4`. */
const FILTER_PREFIX = 'f.';
/** Column filters, likewise one parameter per column: `c.name=Framax`. */
const COLUMN_PREFIX = 'c.';

const SHARE_PARAM_NAMES: string[] = Object.values(P);

/** Whether a parameter belongs to this module (CatalogPage removes them once applied). */
export const isShareParam = (name: string) =>
    SHARE_PARAM_NAMES.includes(name) || name.startsWith(FILTER_PREFIX) || name.startsWith(COLUMN_PREFIX);

/** Ranges as `min..max` with either side optional ("2.5..", "..4"); dates the same in ISO form. */
const RANGE_SEPARATOR = '..';

const encodeFilter = (value: FilterValue): string[] => {
    if (typeof value === 'boolean') {
        return value ? ['true'] : [];
    }
    if (Array.isArray(value)) {
        return value;
    }
    if ('from' in value || 'to' in value) {
        const { from, to } = value as DateRangeValue;
        return [`${from ?? ''}${RANGE_SEPARATOR}${to ?? ''}`];
    }
    const { min, max } = value as RangeValue;
    return [`${min ?? ''}${RANGE_SEPARATOR}${max ?? ''}`];
};

/** The state as URL parameters. Lists are repeated parameters (`scope=a&scope=b`) rather than a
 * joined string, so no id or value can ever collide with a separator. */
export const toSearchParams = (state: CatalogSearchState): URLSearchParams => {
    const params = new URLSearchParams();
    if (state.mode !== DEFAULTS.mode) {
        params.set(P.mode, state.mode);
    }
    if (state.search.trim()) {
        params.set(P.search, state.search.trim());
    }
    if (state.nodeId) {
        params.set(P.nodeId, state.nodeId);
    }
    if (state.documentNodeId) {
        params.set(P.documentNodeId, state.documentNodeId);
    }
    state.scopeNodeIds.forEach((id) => params.append(P.scopeNodeIds, id));
    Object.entries(state.filters).forEach(([key, value]) =>
        encodeFilter(value).forEach((entry) => params.append(`${FILTER_PREFIX}${key}`, entry))
    );
    if (state.documentLanguage) {
        params.set(P.documentLanguage, state.documentLanguage);
    }
    state.documentDocTypes.forEach((value) => params.append(P.documentDocTypes, value));
    state.documentEditions.forEach((value) => params.append(P.documentEditions, value));
    if (state.sort.key !== DEFAULTS.sort.key || state.sort.dir !== DEFAULTS.sort.dir) {
        params.set(P.sort, `${state.sort.key}:${state.sort.dir}`);
    }
    if (state.viewMode !== DEFAULTS.viewMode) {
        params.set(P.viewMode, state.viewMode);
    }
    state.listColumns.forEach((key) => params.append(P.listColumns, key));
    Object.entries(state.columnFilters).forEach(([key, value]) => {
        if (value.trim()) {
            params.set(`${COLUMN_PREFIX}${key}`, value.trim());
        }
    });
    return params;
};

/**
 * The state a link carries, or undefined when the URL has none of this module's parameters (a
 * plain /catalog, or just `?preset=`). Missing parameters fall back to the defaults: a link
 * describes the WHOLE search, so whatever it does not mention is meant to be unset.
 *
 * The facet filters come back as plain string lists here - which of them is a range, a date range
 * or a switch only the catalog config knows, so sanitizeSearchState (always applied before a state
 * is used) turns them into their real shape.
 */
export const fromSearchParams = (params: URLSearchParams): CatalogSearchState | undefined => {
    if (![...params.keys()].some(isShareParam)) {
        return undefined;
    }
    const filters: ActiveFilters = {};
    const columnFilters: Record<string, string> = {};
    params.forEach((value, name) => {
        const column = name.slice(COLUMN_PREFIX.length);
        if (name.startsWith(COLUMN_PREFIX) && column && value.trim()) {
            columnFilters[column] = value.trim();
        }
        const key = name.slice(FILTER_PREFIX.length);
        if (name.startsWith(FILTER_PREFIX) && key && value) {
            filters[key] = [...((filters[key] as string[] | undefined) ?? []), value];
        }
    });
    const [sortKey, sortDir] = (params.get(P.sort) ?? '').split(':');
    return {
        mode: params.get(P.mode) === 'documents' ? 'documents' : 'products',
        search: params.get(P.search) ?? '',
        nodeId: params.get(P.nodeId) || undefined,
        documentNodeId: params.get(P.documentNodeId) || undefined,
        scopeNodeIds: params.getAll(P.scopeNodeIds).filter(Boolean),
        filters,
        documentLanguage: params.get(P.documentLanguage) || null,
        documentDocTypes: params.getAll(P.documentDocTypes).filter(Boolean),
        documentEditions: params.getAll(P.documentEditions).filter(Boolean),
        sort: sortKey ? { key: sortKey, dir: sortDir === 'desc' ? 'desc' : 'asc' } : DEFAULTS.sort,
        viewMode: params.get(P.viewMode) === 'grid' ? 'grid' : 'list',
        listColumns: params.getAll(P.listColumns).filter(Boolean),
        columnFilters
    };
};

const splitRange = (raw: string): [string, string] | undefined => {
    const at = raw.indexOf(RANGE_SEPARATOR);
    return at < 0 ? undefined : [raw.slice(0, at).trim(), raw.slice(at + RANGE_SEPARATOR.length).trim()];
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * One filter in the shape its facet expects, or undefined if it does not fit - a link is user
 * input and may have been edited by hand. Accepts the real shapes too, so an already sanitized
 * state passes through unchanged.
 */
const toFacetValue = (facet: FacetDef, value: FilterValue): FilterValue | undefined => {
    const first = Array.isArray(value) ? value[0] : undefined;
    if (facet.type === 'value-list') {
        const values = Array.isArray(value) ? value.filter((entry) => typeof entry === 'string' && entry) : [];
        return values.length > 0 ? values : undefined;
    }
    if (facet.type === 'boolean') {
        return value === true || first === 'true' ? true : undefined;
    }
    if (facet.type === 'range') {
        if (!Array.isArray(value) && typeof value === 'object') {
            return value;
        }
        const parts = first !== undefined ? splitRange(first) : undefined;
        const [min, max] = (parts ?? []).map((part) => (part === '' ? undefined : Number(part)));
        if (
            [min, max].some((n) => n !== undefined && !Number.isFinite(n)) ||
            (min === undefined && max === undefined)
        ) {
            return undefined;
        }
        return { min, max };
    }
    if (facet.type === 'date-range') {
        if (!Array.isArray(value) && typeof value === 'object') {
            return value;
        }
        const parts = first !== undefined ? splitRange(first) : undefined;
        const [from, to] = (parts ?? []).map((part) => (part === '' ? undefined : part));
        if ([from, to].some((d) => d !== undefined && !ISO_DATE.test(d)) || (!from && !to)) {
            return undefined;
        }
        return { from, to };
    }
    return undefined;
};

/** A stored profile's query back to a state - an empty query is the plain, unfiltered catalog. */
export const searchStateFromQuery = (query: string): CatalogSearchState =>
    fromSearchParams(new URLSearchParams(query)) ?? { ...DEFAULTS };

/**
 * Brings the facet filters into their real shapes (see fromSearchParams) and drops whatever the
 * current catalog no longer knows - a facet removed from the config, a tree node
 * that left the data since the link was made or the profile saved. Kept would they show up as raw
 * keys in the filter chips, or select a node nobody can see.
 */
export const sanitizeSearchState = (
    state: CatalogSearchState,
    config: CatalogConfig,
    nodeExists: (nodeId: string) => boolean
): CatalogSearchState => {
    const facetKeys = new Set(config.facets.map((facet) => facet.key));
    const filters: ActiveFilters = {};
    config.facets.forEach((facet) => {
        const raw = state.filters[facet.key];
        const value = raw === undefined ? undefined : toFacetValue(facet, raw);
        if (value !== undefined) {
            filters[facet.key] = value;
        }
    });
    return {
        ...state,
        nodeId: state.nodeId && nodeExists(state.nodeId) ? state.nodeId : undefined,
        documentNodeId: state.documentNodeId && nodeExists(state.documentNodeId) ? state.documentNodeId : undefined,
        scopeNodeIds: state.scopeNodeIds.filter(nodeExists),
        filters,
        listColumns: state.listColumns.filter((key) => facetKeys.has(key)),
        // The fixed columns plus any attribute column the config knows.
        columnFilters: Object.fromEntries(
            Object.entries(state.columnFilters ?? {}).filter(
                ([key, value]) =>
                    typeof value === 'string' &&
                    value.trim() !== '' &&
                    (['name', 'artNr', 'category'].includes(key) || facetKeys.has(key))
            )
        )
    };
};

/** The link for a state: this app's /catalog route (wherever the app is mounted, and also when an
 * article overlay is open on top of the list), plus the parameters. */
export const shareUrl = (state: CatalogSearchState): string => {
    const path = window.location.pathname.replace(/\/catalog(\/.*)?$/, '/catalog');
    const query = toSearchParams(state).toString();
    return `${window.location.origin}${path}${query ? `?${query}` : ''}`;
};

/** The current search state, and a way to restore one - both straight on the catalog atoms. */
export const useCatalogSearchState = () => {
    const [mode, setMode] = useAtom(searchModeAtom);
    const [search, setSearch] = useAtom(searchAtom);
    const [nodeId, setNodeId] = useAtom(selectedNodeIdAtom);
    const [documentNodeId, setDocumentNodeId] = useAtom(selectedDocumentNodeIdAtom);
    const [scopeNodeIds, setScopeNodeIds] = useAtom(scopeNodeIdsAtom);
    const [filters, setFilters] = useAtom(filtersAtom);
    const [documentLanguage, setDocumentLanguage] = useAtom(documentLanguageAtom);
    const [documentDocTypes, setDocumentDocTypes] = useAtom(documentDocTypesAtom);
    const [documentEditions, setDocumentEditions] = useAtom(documentEditionsAtom);
    const [sort, setSort] = useAtom(sortAtom);
    const [viewMode, setViewMode] = useAtom(viewModeAtom);
    const [listColumns, setListColumns] = useAtom(listColumnsAtom);
    const [columnFilters, setColumnFilters] = useAtom(columnFiltersAtom);
    const [, setPage] = useAtom(pageAtom);

    const current = useMemo<CatalogSearchState>(
        () => ({
            mode,
            search,
            nodeId,
            documentNodeId,
            scopeNodeIds,
            filters,
            documentLanguage,
            documentDocTypes,
            documentEditions,
            sort,
            viewMode,
            listColumns,
            columnFilters
        }),
        [
            mode,
            search,
            nodeId,
            documentNodeId,
            scopeNodeIds,
            filters,
            documentLanguage,
            documentDocTypes,
            documentEditions,
            sort,
            viewMode,
            listColumns,
            columnFilters
        ]
    );

    const apply = useCallback(
        (state: CatalogSearchState) => {
            setMode(state.mode);
            setSearch(state.search);
            setNodeId(state.nodeId);
            setDocumentNodeId(state.documentNodeId);
            setScopeNodeIds(state.scopeNodeIds);
            setFilters(state.filters);
            setDocumentLanguage(state.documentLanguage);
            setDocumentDocTypes(state.documentDocTypes);
            setDocumentEditions(state.documentEditions);
            setSort(state.sort);
            setViewMode(state.viewMode);
            setListColumns(state.listColumns);
            setColumnFilters(state.columnFilters ?? {});
            setPage(1);
        },
        [
            setMode,
            setSearch,
            setNodeId,
            setDocumentNodeId,
            setScopeNodeIds,
            setFilters,
            setDocumentLanguage,
            setDocumentDocTypes,
            setDocumentEditions,
            setSort,
            setViewMode,
            setListColumns,
            setColumnFilters,
            setPage
        ]
    );

    return { current, apply };
};

// ---- Suchprofile: named searches, stored in this browser ---------------------------------------

export interface SearchProfile {
    id: string;
    name: string;
    /** ISO timestamp - newest first in the menu. */
    savedAt: string;
    /** Stored as the link's query string rather than the object: the same validation path as a
     * shared link on the way back in, and a stored profile survives changes to the state shape. */
    query: string;
}

/** Enough for any real use; the oldest drop out beyond it, so localStorage cannot grow unbounded. */
export const MAX_SEARCH_PROFILES = 30;

/**
 * Saved searches of THIS browser (localStorage) - no account and no server involved, which is what
 * makes it work in the offline catalog export too. The flip side, said in the menu as well: they do
 * not travel to another device or browser; a shared link does.
 */
export const searchProfilesAtom = atomWithStorage<SearchProfile[]>('pih.searchProfiles', [], undefined, {
    getOnInit: true
});

export const newProfileId = () =>
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
